import React, { useState, useContext } from "react";
import { Button, Modal, Card, Descriptions, Empty, Grid, Tag, message } from "antd";
import moment from 'moment';
import { useDispatch } from "react-redux";
import { fetchTripSheet } from "../../actions/reports.action";
import { loadingAction } from "../../actions/loading-actions";
import { isValid, isValidArray, isValidNumber } from "../../utils/utilities";
import { getLocalDateAndTime } from "../../utils/date.utils";
import { DeviceContext } from "../device-provider/device-provider";

const { useBreakpoint } = Grid;

const groupTravellersByTicket = (tripSheet) => {
    if (!isValidArray(tripSheet)) {
        return [];
    }
    const ticketsByPnr = new Map();
    tripSheet.forEach((row) => {
        if (row.traveller_status !== "Confirmed" || !isValid(row.booking_time)) {
            return;
        }
        const amountPaid = isValidNumber(row.amount_paid) ? Number(row.amount_paid) : 0;
        const existing = ticketsByPnr.get(row.pnr);
        if (existing) {
            existing.travellers.push(row);
            existing.totalAmountPaid += amountPaid;
        } else {
            ticketsByPnr.set(row.pnr, {
                pnr: row.pnr,
                booking_time: row.booking_time,
                agent_name: row.agent_name,
                phone_number: row.phone_number,
                travellers: [row],
                totalAmountPaid: amountPaid
            });
        }
    });
    return Array.from(ticketsByPnr.values());
};

const getRecentTickets = (tripSheet, limit) => {
    return groupTravellersByTicket(tripSheet)
        .sort((a, b) => moment(b.booking_time).unix() - moment(a.booking_time).unix())
        .slice(0, limit);
};

const TicketCard = ({ ticket }) => {
    const seatLabel = ticket.travellers.length === 1 ? 'Seat' : 'Seats';
    const seatNumbers = ticket.travellers.map((t) => t.seat_number).join(', ');
    const passengerNames = ticket.travellers.map((t) => t.full_name).filter(Boolean).join(', ');
    return (
        <Card
            size="small"
            style={{ marginBottom: 12 }}
            title={
                <span>
                    <Tag color="blue" style={{ marginRight: 8 }}>PNR {ticket.pnr}</Tag>
                    <span style={{ color: '#888', fontSize: 12 }}>
                        {ticket.travellers.length} {ticket.travellers.length === 1 ? 'passenger' : 'passengers'}
                    </span>
                </span>
            }
            extra={
                <span style={{ fontSize: 12, color: '#888' }}>
                    {isValid(ticket.booking_time) ? getLocalDateAndTime(ticket.booking_time) : '-'}
                </span>
            }
        >
            <Descriptions size="small" column={1} colon={false} labelStyle={{ width: 150, color: '#888' }}>
                <Descriptions.Item label={seatLabel}>{seatNumbers || '-'}</Descriptions.Item>
                <Descriptions.Item label="Passengers">{passengerNames || '-'}</Descriptions.Item>
                <Descriptions.Item label="Phone">{ticket.phone_number || '-'}</Descriptions.Item>
                <Descriptions.Item label="Total Amount Paid">{ticket.totalAmountPaid || '-'}</Descriptions.Item>
                <Descriptions.Item label="Agent">{ticket.agent_name || '-'}</Descriptions.Item>
            </Descriptions>
        </Card>
    );
};

export const LastBookedSeatsComponent = (props) => {
    const { tripId, className } = props;

    const { isMobile } = useContext(DeviceContext);
    const screens = useBreakpoint();
    const isCompactView = isMobile || !screens.md;

    const [visible, setVisible] = useState(false);
    const [tickets, setTickets] = useState([]);

    const dispatch = useDispatch();
    const setLoading = (bool) => dispatch(loadingAction(bool));

    const onClick = () => {
        if (!isValid(tripId)) {
            message.warn("Trip not available");
            return;
        }
        setVisible(true);
        setLoading(true);
        fetchTripSheet(tripId, (data) => {
            setTickets(getRecentTickets(data && data.tripSheet, 2));
            setLoading(false);
        }, () => {
            setLoading(false);
        });
    };

    return (
        <span style={{ marginLeft: 8 }}>
            <Button
                className={`outline-button ${className || ''}`}
                onClick={onClick}
            >
                {isCompactView ? 'Recent' : 'Recent Bookings'}
            </Button>
            <Modal
                title="Recent Bookings"
                maskClosable={false}
                visible={visible}
                width={480}
                onCancel={() => setVisible(false)}
                footer={null}
                bodyStyle={{ padding: 16 }}
            >
                {isValidArray(tickets) && tickets.length > 0 ? (
                    tickets.map((ticket) => (
                        <TicketCard key={ticket.pnr} ticket={ticket} />
                    ))
                ) : (
                    <Empty description="No bookings yet" />
                )}
            </Modal>
        </span>
    );
};
